import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClipboardCheck, Loader2, Users } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import { useReducedMotion } from "../../lib/motion.js";
import { CampaignAudienceSummary } from "./CampaignAudienceSummary.js";
import { CampaignDraftSection } from "./CampaignDraftSection.js";
import { CampaignDraftShell, CampaignPostLaunchShell } from "./CampaignDraftShell.js";
import { CampaignDraftStepper } from "./CampaignDraftStepper.js";
import { CampaignEmptyAudienceState } from "./CampaignEmptyAudienceState.js";
import { CampaignHeader } from "./CampaignHeader.js";
import {
  CampaignManageRecipientsButton,
  campaignAudiencePrimaryActionClass,
} from "./CampaignManageRecipientsButton.js";
import { CampaignManageRecipientsDialog } from "./CampaignManageRecipientsDialog.js";
import { CampaignMessageStep } from "./CampaignMessageStep.js";
import { CampaignNotFoundState } from "./CampaignNotFoundState.js";
import { CampaignPostLaunchWorkspace } from "./CampaignPostLaunchWorkspace.js";
import { CampaignReviewStep } from "./CampaignReviewStep.js";
import { CampaignRecipientsTable } from "./CampaignRecipientsTable.js";
import { useCampaignWorkspace } from "./CampaignWorkspaceLayout.js";
import { useNotifications } from "../notifications/notification-context.js";
import { mapCampaignError } from "./campaigns-errors.js";
import {
  campaignContentHeaderStatus,
  canOpenCampaignMessageStep,
} from "./campaign-message-presentation.js";
import { CampaignSectionStatusBadge } from "./CampaignSectionStatusBadge.js";
import { canOpenCampaignReviewStep } from "./campaign-review-presentation.js";
import {
  CAMPAIGN_DRAFT_STEPS,
  campaignDraftStepLocked,
  campaignViewedSectionFromScroll,
  isCampaignPostLaunchStatus,
  type CampaignDraftStepId,
} from "./campaign-draft-workflow.js";
import {
  audienceNeedsReview,
  campaignHasAudienceSources,
  campaignListHref,
  campaignSourceCounts,
  canMutateCampaignAudience,
  type CampaignManageView,
} from "./campaigns-presentation.js";
import { useCampaignEditor } from "./use-campaign-editor.js";

const CAMPAIGN_REVIEW_FOCUS_SETTLE_MS = 150;
const CAMPAIGN_REVIEW_FOCUS_MAX_WAIT_MS = 1_500;

export function CampaignDetailPage({
  organizationId,
  campaignId,
  role,
}: {
  organizationId: number;
  campaignId: number;
  role: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { pushNotification } = useNotifications();
  const { list, openMobileNav } = useCampaignWorkspace();
  const editor = useCampaignEditor(organizationId, campaignId);
  const [manageOpen, setManageOpen] = useState(false);
  const [manageView, setManageView] = useState<CampaignManageView>("hub");
  const [hadConfirmedSnapshot, setHadConfirmedSnapshot] = useState(false);
  const [viewedSection, setViewedSection] = useState<CampaignDraftStepId>("audience");
  const [messageDraftDirty, setMessageDraftDirty] = useState(false);
  const [contentSaveSlot, setContentSaveSlot] = useState<HTMLDivElement | null>(null);
  const [reviewActionsSlot, setReviewActionsSlot] = useState<HTMLDivElement | null>(null);
  const [reviewStatusSlot, setReviewStatusSlot] = useState<HTMLDivElement | null>(null);
  const [reviewLaunchReady, setReviewLaunchReady] = useState<boolean | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion() ?? false;
  const canMutate = canMutateCampaignAudience(role);
  const campaign = editor.campaign;
  const canMutateDefinition = canMutate && campaign?.status === "DRAFT";
  const wasPostLaunchRef = useRef(false);
  const pendingReviewFocusRef = useRef(false);
  const definition = campaign?.audienceDefinition;
  const confirmed = Boolean(campaign && !campaign.snapshotStale);
  const needsReview = audienceNeedsReview({
    snapshotStale: Boolean(campaign?.snapshotStale),
    hadConfirmedSnapshot:
      hadConfirmedSnapshot || editor.upload?.status === "CONSUMED",
  });
  const hasSources = definition ? campaignHasAudienceSources(definition) : false;
  const sourceCounts = definition ? campaignSourceCounts(definition) : null;
  const selectedGroupIds = new Set(definition?.contactGroupIds ?? []);
  const selectedGroups = editor.groups.filter((group) => selectedGroupIds.has(group.id));
  const recipientCount = campaign?.audienceSnapshotSummary?.readyRecipients ?? 0;
  const emptyAudience = !hasSources && !confirmed;
  const audienceReady = confirmed && !needsReview;
  const messageUnlocked =
    canOpenCampaignMessageStep({
      snapshotStale: Boolean(campaign?.snapshotStale),
      readyRecipientCount: recipientCount,
    }) ||
    hadConfirmedSnapshot ||
    Boolean(campaign?.messageConfigured) ||
    campaign?.whatsappTemplateId != null;
  const reviewUnlocked = Boolean(
    campaign &&
      canOpenCampaignReviewStep({
        status: campaign.status,
        snapshotStale: campaign.snapshotStale,
        readyRecipientCount: recipientCount,
        messageConfigured: campaign.messageConfigured,
      }),
  );
  const postLaunch = Boolean(campaign && isCampaignPostLaunchStatus(campaign.status));

  useEffect(() => {
    if (!campaign) return;
    const nextPostLaunch = isCampaignPostLaunchStatus(campaign.status);
    if (wasPostLaunchRef.current && !nextPostLaunch && reviewUnlocked) {
      pendingReviewFocusRef.current = true;
    }
    wasPostLaunchRef.current = nextPostLaunch;
  }, [campaign, reviewUnlocked]);

  useEffect(() => {
    const root = scrollRef.current;
    if (postLaunch || !root || !pendingReviewFocusRef.current) return;
    // The draft sections load their own data after mounting, so wait until the layout stops
    // growing (bounded) and then scroll to Review exactly once.
    let settleTimer = 0;
    const focusReview = () => {
      cleanup();
      const node = root.querySelector(`#${campaignSectionId("review")}`);
      if (!(node instanceof HTMLElement)) return;
      pendingReviewFocusRef.current = false;
      node.scrollIntoView({ behavior: "auto", block: "start" });
      node.focus({ preventScroll: true });
      setViewedSection("review");
    };
    const scheduleSettle = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(focusReview, CAMPAIGN_REVIEW_FOCUS_SETTLE_MS);
    };
    const userTookOver = () => {
      pendingReviewFocusRef.current = false;
      cleanup();
    };
    const observer = new ResizeObserver(scheduleSettle);
    for (const child of Array.from(root.children)) observer.observe(child);
    const maxTimer = window.setTimeout(focusReview, CAMPAIGN_REVIEW_FOCUS_MAX_WAIT_MS);
    root.addEventListener("wheel", userTookOver, { passive: true, once: true });
    root.addEventListener("touchstart", userTookOver, { passive: true, once: true });
    const cleanup = () => {
      observer.disconnect();
      window.clearTimeout(settleTimer);
      window.clearTimeout(maxTimer);
      root.removeEventListener("wheel", userTookOver);
      root.removeEventListener("touchstart", userTookOver);
    };
    scheduleSettle();
    return cleanup;
  }, [postLaunch]);

  useEffect(() => {
    if (campaign && !campaign.snapshotStale) {
      setHadConfirmedSnapshot(true);
    }
  }, [campaign]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const rootTop = root.getBoundingClientRect().top;
      const sections = CAMPAIGN_DRAFT_STEPS.flatMap((step) => {
        const node = root.querySelector(`#${campaignSectionId(step)}`);
        return node instanceof HTMLElement
          ? [{ step, top: node.getBoundingClientRect().top - rootTop }]
          : [];
      });
      if (sections.length === 0) return;
      const scrollable = root.scrollHeight > root.clientHeight + 1;
      setViewedSection(
        campaignViewedSectionFromScroll({
          sections,
          viewportHeight: root.clientHeight,
          atBottom: scrollable && root.scrollTop + root.clientHeight >= root.scrollHeight - 2,
          messageUnlocked,
          reviewUnlocked,
        }),
      );
    };
    const schedule = () => {
      if (frame === 0) frame = window.requestAnimationFrame(update);
    };
    schedule();
    root.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame);
      root.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [messageUnlocked, reviewUnlocked, campaign]);

  const openManage = (view: CampaignManageView) => {
    setManageView(view);
    setManageOpen(true);
    if (view === "review") {
      void editor.previewAudience();
    }
  };

  const confirmAudience = async () => {
    try {
      await editor.confirmAudience();
      pushNotification({ message: t("campaigns.toasts.audienceConfirmed") });
      await list.reload({ silent: true });
    } catch (caught) {
      pushNotification({
        kind: "error",
        message: mapCampaignError(t, caught, t("campaigns.errors.confirmFailed")),
      });
    }
  };

  const scrollToSection = (step: CampaignDraftStepId) => {
    if (campaignDraftStepLocked(step, { messageUnlocked, reviewUnlocked })) return;
    const node = scrollRef.current?.querySelector(`#${campaignSectionId(step)}`);
    if (!(node instanceof HTMLElement)) return;
    node.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  const focusSection = (step: CampaignDraftStepId) => {
    if (campaignDraftStepLocked(step, { messageUnlocked, reviewUnlocked })) return;
    scrollToSection(step);
    const node = scrollRef.current?.querySelector(`#${campaignSectionId(step)}`);
    if (node instanceof HTMLElement) node.focus({ preventScroll: true });
  };

  if (editor.loading && !campaign) {
    return (
      <div className="grid h-full place-items-center" data-testid="campaign-detail-loading">
        <p className="flex items-center gap-2 text-muted" aria-live="polite">
          <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          {t("campaigns.loading")}
        </p>
      </div>
    );
  }

  if (!campaign || !definition) {
    return (
      <div className="grid h-full place-items-center p-5" data-testid="campaign-detail-missing">
        <CampaignNotFoundState
          listHref={campaignListHref({
            status: list.status,
            search: list.search,
            page: list.page,
            pageSize: list.pageSize,
            sort: list.sort,
          })}
        />
      </div>
    );
  }

  const header = (
    <CampaignHeader
      name={campaign.name}
      status={campaign.status}
      confirmed={confirmed}
      recipientCount={recipientCount}
      saving={editor.saving || editor.uploading}
      onOpenMobileNav={openMobileNav}
      actions={null}
    />
  );

  const reviewStep = (
    <CampaignReviewStep
      organizationId={organizationId}
      campaign={campaign}
      canMutate={canMutate}
      contentDraftDirty={messageDraftDirty}
      messageUnlocked={messageUnlocked}
      actionsSlot={reviewActionsSlot}
      statusSlot={reviewStatusSlot}
      onNavigateToStep={focusSection}
      onLaunchReadinessChange={setReviewLaunchReady}
      onCampaignChanged={async () => {
        await editor.reload();
        await list.reload({ silent: true });
      }}
    />
  );

  const manageDialog = (
    <CampaignManageRecipientsDialog
      open={manageOpen}
      view={manageView}
      organizationId={organizationId}
      campaign={campaign}
      groups={editor.groups}
      upload={editor.upload}
      uploadPreview={editor.uploadPreview}
      preview={editor.preview}
      error={editor.error}
      canMutate={canMutateDefinition}
      saving={editor.saving}
      uploading={editor.uploading}
      uploadProgress={editor.uploadProgress}
      polling={editor.polling}
      onOpenChange={setManageOpen}
      onViewChange={setManageView}
      onSetContactIds={async (contactIds) => {
        await editor.setContactIds(contactIds);
        await list.reload({ silent: true });
      }}
      onSetGroupIds={async (groupIds) => {
        await editor.setGroupIds(groupIds);
        await list.reload({ silent: true });
      }}
      onApplyCsv={async (uploadId) => {
        await editor.setCsvUpload(uploadId);
        await list.reload({ silent: true });
      }}
      onUploadCsv={async (file, worksheet) => {
        await editor.uploadCsv(file, worksheet);
        await list.reload({ silent: true });
      }}
      onConfigureUpload={editor.configureUpload}
      onRemoveUpload={async () => {
        await editor.removeUpload();
        await list.reload({ silent: true });
      }}
      onPreview={editor.previewAudience}
      onConfirm={confirmAudience}
    />
  );

  if (postLaunch) {
    return (
      <div className="flex h-full min-h-0 flex-col" data-testid="campaign-detail-page">
        <CampaignPostLaunchShell header={header}>
          <CampaignPostLaunchWorkspace
            organizationId={organizationId}
            campaign={campaign}
            recipients={editor.recipients}
            canMutate={canMutate}
            onCampaignChanged={async () => {
              await editor.reload();
              await list.reload({ silent: true });
            }}
          />
        </CampaignPostLaunchShell>
        {manageDialog}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="campaign-detail-page">
      <CampaignDraftShell
        scrollRef={scrollRef}
        header={header}
        stepper={
          <CampaignDraftStepper
            active={viewedSection}
            messageUnlocked={messageUnlocked}
            reviewUnlocked={reviewUnlocked}
            reviewNeedsAttention={reviewLaunchReady === false}
            onSelectStep={scrollToSection}
          />
        }
      >
        <CampaignDraftSection
          id={campaignSectionId("audience")}
          testId="campaign-section-audience"
          title={t("campaigns.detail.targetAudience")}
          locked={false}
          titleExtra={
            emptyAudience ? null : (
              <>
                <AudienceStatusBadge ready={audienceReady} />
                <span className="hidden h-4 w-px bg-line/80 sm:block" aria-hidden="true" />
                <CampaignAudienceSummary sourceCounts={sourceCounts} groups={selectedGroups} />
              </>
            )
          }
          actions={
            emptyAudience ? null : (
              <div className="flex flex-wrap items-center gap-2">
                <p
                  className="m-0 inline-flex items-center gap-1.5 rounded-full border border-line/70 bg-surface-muted/70 px-2.5 py-[0.1875rem] text-[0.75rem] font-medium text-foreground tabular-nums"
                  data-testid="campaign-audience-recipient-count"
                >
                  <Users size={13} className="shrink-0 text-muted" aria-hidden="true" />
                  {t("campaigns.detail.willReceive", { count: recipientCount })}
                </p>
                {audienceReady && canMutateDefinition ? (
                  <CampaignManageRecipientsButton
                    testId="campaign-audience-manage-recipients"
                    onClick={() => openManage("hub")}
                  />
                ) : null}
              </div>
            )
          }
        >
          {editor.error && !manageOpen ? (
            <p className="m-0 rounded-md border border-error/40 bg-error-muted px-3 py-2 text-[0.8125rem] text-error" role="alert">
              {mapCampaignError(t, editor.error, t("campaigns.errors.requestFailed"))}
            </p>
          ) : null}
          {!emptyAudience && !audienceReady ? (
            <div
              className="mx-auto grid w-full max-w-md justify-items-center gap-3 py-5 text-center"
              data-testid="campaign-audience-stale"
            >
              <p className="m-0 text-[0.8125rem] leading-relaxed text-muted">
                {needsReview
                  ? t("campaigns.detail.staleBannerBody")
                  : t("campaigns.detail.unconfirmedBody")}
              </p>
              {canMutateDefinition ? (
                <Button
                  type="button"
                  size="sm"
                  className={campaignAudiencePrimaryActionClass}
                  data-testid="campaign-audience-blocking-cta"
                  onClick={() => openManage("review")}
                >
                  <ClipboardCheck size={14} aria-hidden="true" />
                  {t("campaigns.detail.reviewAudience")}
                </Button>
              ) : null}
            </div>
          ) : null}
          {!canMutate ? (
            <p className="m-0 rounded-lg border border-line/70 bg-surface px-3 py-2 text-[0.8125rem] text-muted">
              {t("campaigns.detail.readOnly")}
            </p>
          ) : null}
          {emptyAudience ? (
            <CampaignEmptyAudienceState
              canMutate={canMutateDefinition}
              onManageRecipients={() => openManage("hub")}
            />
          ) : null}
          {confirmed && !reviewUnlocked ? (
            <p className="m-0 text-[0.75rem] text-muted" data-testid="campaign-next-is-message">
              {t("campaigns.prelaunch.nextIsMessage")}
            </p>
          ) : null}
          {confirmed ? (
            <section className="flex min-h-0 min-w-0 flex-col" data-testid="campaign-confirmed-recipients">
              <CampaignRecipientsTable
                items={editor.recipients}
                loading={editor.recipientsLoading}
                hasPrevious={editor.recipientsHasPrevious}
                hasNext={editor.recipientsNextCursor != null}
                pageSize={editor.recipientPageSize}
                rangeStart={editor.recipientRangeStart}
                rangeEnd={editor.recipientRangeEnd}
                total={recipientCount}
                onPageSizeChange={editor.setRecipientPageSize}
                onPrevious={() => void editor.loadPreviousRecipients()}
                onNext={() => void editor.loadNextRecipients()}
              />
            </section>
          ) : null}
        </CampaignDraftSection>

        <CampaignDraftSection
          id={campaignSectionId("message")}
          testId="campaign-section-message"
          title={t("campaigns.content.title")}
          titleExtra={
            <ContentStatusBadge
              messageConfigured={campaign.messageConfigured}
              draftDirty={messageDraftDirty}
            />
          }
          actions={
            <div
              ref={setContentSaveSlot}
              className="flex items-center empty:hidden"
              data-testid="campaign-content-actions"
            />
          }
          locked={!messageUnlocked}
          lockMessage={t("campaigns.message.stepLocked")}
        >
          {messageUnlocked ? (
            <CampaignMessageStep
              organizationId={organizationId}
              role={role}
              campaign={campaign}
              recipients={editor.recipients}
              upload={editor.upload}
              canMutate={canMutateDefinition}
              saveActionSlot={contentSaveSlot}
              onDraftDirtyChange={setMessageDraftDirty}
              onCampaignUpdated={(next) => {
                editor.applyCampaignUpdate(next);
                void list.reload({ silent: true });
              }}
            />
          ) : null}
        </CampaignDraftSection>

        <CampaignDraftSection
          id={campaignSectionId("review")}
          testId="campaign-section-review"
          title={t("campaigns.steps.review")}
          titleExtra={
            <div
              ref={setReviewStatusSlot}
              className="flex items-center empty:hidden"
              data-testid="campaign-review-status-slot"
            />
          }
          actions={
            <div
              ref={setReviewActionsSlot}
              className="flex w-full min-w-0 items-center empty:hidden sm:w-auto"
              data-testid="campaign-review-actions"
            />
          }
          locked={false}
        >
          {reviewStep}
        </CampaignDraftSection>
      </CampaignDraftShell>
      {manageDialog}
    </div>
  );
}

function AudienceStatusBadge({ ready }: { ready: boolean }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <CampaignSectionStatusBadge
      ready={ready}
      readyLabel={t("campaigns.detail.statusReady")}
      pendingLabel={t("campaigns.detail.statusNeedsReview")}
      testId="campaign-audience-status"
    />
  );
}

function ContentStatusBadge({
  messageConfigured,
  draftDirty,
}: {
  messageConfigured: boolean;
  draftDirty: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const status = campaignContentHeaderStatus({ messageConfigured, draftDirty });
  return (
    <>
      <CampaignSectionStatusBadge
        ready={status.tone === "ready"}
        readyLabel={t("campaigns.detail.statusReady")}
        pendingLabel={t("campaigns.content.statusIncomplete")}
        testId="campaign-content-status"
      />
      {status.unsaved ? (
        <span
          className="text-[0.75rem] text-muted"
          data-testid="campaign-content-unsaved"
          aria-live="polite"
        >
          {t("campaigns.content.unsavedChanges")}
        </span>
      ) : null}
    </>
  );
}

function campaignSectionId(step: CampaignDraftStepId): string {
  switch (step) {
    case "audience":
      return "campaign-section-audience";
    case "message":
      return "campaign-section-message";
    case "review":
      return "campaign-section-review";
    default: {
      const _exhaustive: never = step;
      return _exhaustive;
    }
  }
}
